import { useCallback, useEffect, useState } from 'react';
import { setlistsApi } from '@/api/setlists';
import { apiErrorMessage } from '@/utils/apiError';
import type { Setlist } from '@/types/setlist';

interface Props {
  teamId: string;
  canWrite: boolean;
  onSelect: (setlist: Setlist) => void;
}

export default function SetlistList({ teamId, canWrite, onSelect }: Props) {
  const [setlists, setSetlists] = useState<Setlist[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const fetchSetlists = useCallback(async (q = search) => {
    try {
      const res = await setlistsApi.list(teamId, q || undefined);
      setSetlists(res.data);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [search, teamId]);

  useEffect(() => {
    fetchSetlists('');
  }, [fetchSetlists]);

  const handleSearch = (v: string) => {
    setSearch(v);
    fetchSetlists(v);
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const res = await setlistsApi.create(teamId, { name: newName.trim() });
      setSetlists((prev) => [res.data, ...prev]);
      setNewName('');
      setCreating(false);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-4 border-b border-white/5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-white/70 text-xs font-semibold uppercase tracking-wider">Setlists</h2>
          {canWrite && (
            <button
              onClick={() => setCreating(true)}
              className="w-7 h-7 flex items-center justify-center bg-[#C8A96E]/20 hover:bg-[#C8A96E]/30 rounded-lg transition-colors"
            >
              <i className="ri-add-line text-[#C8A96E] text-sm"></i>
            </button>
          )}
        </div>
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm"></i>
          <input
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Buscar setlists..."
            className="w-full bg-white/5 border border-white/5 rounded-lg pl-9 pr-4 py-2 text-white text-sm placeholder-white/20 outline-none focus:border-white/20 transition-colors"
          />
        </div>
      </div>

      {creating && (
        <div className="px-5 py-3 border-b border-white/5 flex gap-2">
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setCreating(false); }}
            placeholder="Nome do setlist..."
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm outline-none focus:border-[#C8A96E]/50"
          />
          <button onClick={handleCreate} className="px-3 py-1.5 bg-[#C8A96E] text-black text-xs rounded-lg font-medium hover:bg-[#D4B87A]">
            Criar
          </button>
          <button onClick={() => setCreating(false)} className="px-2 text-white/40 hover:text-white/70 text-sm">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {error && <p className="text-red-400 text-xs px-5 py-2">{error}</p>}

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-5 h-5 border-2 border-white/20 border-t-white/60 rounded-full animate-spin"></div>
          </div>
        ) : setlists.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <i className="ri-list-check-2 text-white/20 text-3xl mb-2"></i>
            <p className="text-white/30 text-sm">
              {search ? 'Nenhum setlist encontrado.' : 'Nenhum setlist ainda.'}
            </p>
          </div>
        ) : (
          setlists.map((sl) => (
            <button
              key={sl.id}
              onClick={() => onSelect(sl)}
              className="w-full flex items-center gap-3 px-5 py-3 hover:bg-white/5 transition-colors text-left group"
            >
              <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                <i className="ri-list-check-2 text-white/40 text-sm"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{sl.name}</p>
                <p className="text-white/30 text-xs">
                  {new Date(sl.created_at).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <i className="ri-arrow-right-s-line text-white/20 group-hover:text-white/50 transition-colors"></i>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
